import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { fmtINR, fmtNum, fmtDate } from '@/lib/api';
import { Loading, EmptyState, SectionTitle, StatusBadge } from '@/components/Shared';
import { Button } from '@/components/ui/button';
import { AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog';
import { ShoppingCart, RotateCw, XCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function CustomerOrders() {
  const nav = useNavigate();
  const [orders, setOrders] = useState(null);
  const load = useCallback(() => api.get('/orders/mine').then((r) => setOrders(r.data)), []);
  useEffect(() => { load(); }, [load]);
  if (!orders) return <Loading />;
  const reorder = (id) => api.post(`/orders/${id}/reorder`).then((r) => { toast.success('Reordered'); nav(`/app/track/${r.data.id}`); });
  const cancelOrder = async (o) => {
    try { await api.post(`/orders/${o.id}/status`, { status: 'cancelled' }); toast.success(`Order ${o.order_no} cancelled`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Unable to cancel'); }
  };
  const cancellable = (o) => ['placed', 'confirmed'].includes(o.status);
  return (
    <div className="space-y-4">
      <SectionTitle action={<Button size="sm" onClick={() => nav('/app/catalog')}><ShoppingCart className="h-4 w-4 mr-1" />New Order</Button>}>My Orders</SectionTitle>
      {orders.length === 0 ? <EmptyState title="No orders yet" desc="Place your first diesel order." action={<Button onClick={() => nav('/app/order/diesel-hsd')}>Order Diesel Now</Button>} /> : (
        <div className="space-y-2">
          {orders.map((o) => (
            <div key={o.id} className="rounded-xl border border-border bg-card p-4" data-testid={`order-row-${o.order_no}`}>
              <div className="flex items-center justify-between">
                <div className="cursor-pointer" onClick={() => nav(`/app/track/${o.id}`)}><p className="font-medium">{o.order_no}</p><p className="text-xs text-muted-foreground">{fmtDate(o.created_at)} · {fmtNum(o.pricing.qty)} {o.pricing.unit}</p></div>
                <StatusBadge status={o.status} label={o.status_label} />
              </div>
              <div className="mt-2 flex items-center justify-between">
                <span className="font-semibold">{fmtINR(o.pricing.total)}</span>
                <div className="flex gap-2">
                  {cancellable(o) && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button size="sm" variant="outline" data-testid={`cancel-${o.order_no}`}><XCircle className="h-4 w-4 mr-1 text-destructive" />Cancel</Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Cancel order {o.order_no}?</AlertDialogTitle><AlertDialogDescription>This cannot be undone. Orders can only be cancelled before a driver is dispatched.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Keep Order</AlertDialogCancel><AlertDialogAction onClick={() => cancelOrder(o)} data-testid={`confirm-cancel-${o.order_no}`}>Cancel Order</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                  <Button size="sm" variant="secondary" onClick={() => reorder(o.id)} data-testid={`reorder-${o.order_no}`}><RotateCw className="h-4 w-4 mr-1" />Reorder</Button>
                  <Button size="sm" onClick={() => nav(`/app/track/${o.id}`)}>Track</Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
